import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Bell, FileSpreadsheet, Search, 
  Building2, ShieldAlert, RefreshCw, Mail, Phone,
  AlertTriangle, BellRing, ScanLine, ChevronRight
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { tagClass, PREDEFINED_TAGS } from "../components/TagPicker";
import TeamChat from "../components/TeamChat";
import ExportModal from "../components/ExportModal";
import { getLocalLeads, syncLocalLeads } from "../lib/leadCache";
import { syncActiveReminders } from "../services/notificationService";

const INITIAL_PAGE_SIZE = 5;
const CARDS_PER_CLICK = 5;

function LeadCardSkeleton() {
  return (
    <div className="p-5 bg-white border border-slate-200/90 rounded-[24px] shadow-2xs animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-[60px] h-[60px] rounded-[14px] bg-slate-200/90 shrink-0" />
        <div className="space-y-2 min-w-0 flex-1 pt-0.5">
          <div className="flex items-center justify-between gap-2">
            <div className="h-4 bg-slate-200 rounded-md w-36" />
            <div className="h-4 bg-slate-100 rounded-full w-14" />
          </div>
          <div className="h-3.5 bg-slate-200/70 rounded-md w-48" />
          <div className="h-3 bg-slate-100 rounded-md w-40" />
          <div className="h-3 bg-slate-100 rounded-md w-28" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-3.5 pt-3 border-t border-slate-100">
        <div className="h-5 w-16 bg-slate-100 rounded-full" />
        <div className="h-5 w-20 bg-slate-100 rounded-full" />
        <div className="h-5 w-14 bg-slate-100 rounded-full" />
      </div>
    </div>
  );
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("pipeline");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);

  const [stats, setStats] = useState({ 
    total: 0, 
    scans_used: 0, 
    scan_limit: 20, 
    msgs_used: 0, 
    msg_limit: 20,
    plan_name: "Free Trial",
    hotLeads: 0, 
    followUps: 0 
  });
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("All");

  const companyName = user?.company_name || user?.company || user?.organization || "";
  const hasCompany = Boolean(companyName.trim());
  const isMountedRef = useRef(true);

  // 1. FAST LOCAL LOAD: Render cached items immediately & sync reminders
  useEffect(() => {
    isMountedRef.current = true;
    (async () => {
      try {
        const cached = await getLocalLeads(user?.id);
        if (isMountedRef.current && Array.isArray(cached) && cached.length > 0) {
          setLeads(cached.slice(0, INITIAL_PAGE_SIZE));
          
          const hotCount = cached.filter((l) => 
            Array.isArray(l.tags) && l.tags.some((t) => String(t).toLowerCase().includes("hot"))
          ).length;
          const followCount = cached.filter((l) => 
            Boolean(l.reminder_at) || (Array.isArray(l.tags) && l.tags.some((t) => String(t).toLowerCase().includes("follow")))
          ).length;

          setStats((prev) => ({ 
            ...prev, 
            total: cached.length,
            hotLeads: hotCount,
            followUps: followCount
          }));
          setLoading(false);

          // Register native reminders for cached leads
          syncActiveReminders(cached);
        }
      } catch (err) {
        console.warn("Local leads cache read notice:", err);
      }
    })();

    return () => {
      isMountedRef.current = false;
    };
  }, [user?.id]);

  // 2. FETCH INITIAL PAGE & SERVER STATS
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, leadsRes] = await Promise.all([
        api.get("/leads/stats/summary").catch(() => ({ data: null })),
        api.get("/leads", { 
          params: { 
            limit: INITIAL_PAGE_SIZE, 
            offset: 0,
            q: searchQuery.trim() || undefined,
            tag: selectedTag !== "All" ? selectedTag : undefined
          } 
        }).catch(() => ({ data: [] }))
      ]);

      if (!isMountedRef.current) return;

      const rawData = leadsRes?.data;
      const initialBatch = Array.isArray(rawData) ? rawData : (rawData?.leads || []);

      setLeads(initialBatch);
      setHasMore(initialBatch.length === INITIAL_PAGE_SIZE);

      // Schedule any active or upcoming reminders with Android OS
      syncActiveReminders(initialBatch);

      if (statsRes?.data) {
        setStats((prev) => ({
          ...prev,
          total: statsRes.data.total ?? initialBatch.length,
          hotLeads: statsRes.data.hot_leads ?? prev.hotLeads,
          followUps: statsRes.data.follow_ups ?? prev.followUps,
          scans_used: statsRes.data.scans_used ?? prev.scans_used,
          scan_limit: statsRes.data.scan_limit ?? prev.scan_limit,
          msgs_used: statsRes.data.msgs_used ?? prev.msgs_used,
          msg_limit: statsRes.data.msg_limit ?? prev.msg_limit,
          plan_name: statsRes.data.plan_name ?? prev.plan_name
        }));
      }

      if (hasCompany) {
        const teamRes = await api.get("/dashboard/team").catch(() => ({ data: [] }));
        if (isMountedRef.current) setTeam(Array.isArray(teamRes.data) ? teamRes.data : []);
      }
    } catch (err) {
      console.error("Dashboard data load error:", err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [searchQuery, selectedTag, hasCompany]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // 3. PROGRESSIVE ONE-BY-ONE CARD STREAMING
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    const startingOffset = leads.length;

    try {
      for (let i = 0; i < CARDS_PER_CLICK; i++) {
        const currentOffset = startingOffset + i;

        const res = await api.get("/leads", {
          params: {
            limit: 1,
            offset: currentOffset,
            q: searchQuery.trim() || undefined,
            tag: selectedTag !== "All" ? selectedTag : undefined
          }
        }).catch(() => null);

        const rawData = res?.data;
        const nextLead = Array.isArray(rawData) ? rawData[0] : (rawData?.leads?.[0] || null);

        if (nextLead && isMountedRef.current) {
          setLeads((prev) => {
            const exists = prev.some((l) => (l.id || l._id) === (nextLead.id || nextLead._id));
            if (exists) return prev;
            const updated = [...prev, nextLead];
            syncLocalLeads(user?.id, updated);
            return updated;
          });

          // Sync reminder for progressively loaded lead
          if (nextLead.reminder_at) {
            syncActiveReminders([nextLead]);
          }
        } else {
          if (isMountedRef.current) setHasMore(false);
          break;
        }
      }
    } catch (err) {
      console.error("Failed to load cards progressively:", err);
    } finally {
      if (isMountedRef.current) setLoadingMore(false);
    }
  };

  const isUnlimited = stats.plan_name === "Business Annual";
  const isExpired = stats.plan_name?.toLowerCase().includes("expired");
  const usedScans = Number(stats.scans_used) || 0;
  const maxScans = Number(stats.scan_limit) > 0 ? Number(stats.scan_limit) : 20;
  const percentUsed = isUnlimited ? 100 : Math.max(0, Math.min(100, Math.round((usedScans / maxScans) * 100) || 0));

  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-900 pb-32 font-sans">
      <div className="max-w-md mx-auto px-5 pt-8 pb-4">
        
        {/* HEADER */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="text-[9px] font-black tracking-[0.2em] text-slate-600 uppercase block mb-1">
              HI, {user?.name ? user.name.split(" ")[0].toUpperCase() : "EXHIBITOR"}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none text-zinc-950 mt-1">
              Your <br/>
              <span className="text-blue-600">pipeline</span>.
            </h1>
          </div>

          <div className="flex flex-col gap-1.5 shrink-0 pt-1">
            <button
              type="button"
              onClick={() => navigate("/reminders")}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 transition flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Bell className="w-3.5 h-3.5 text-slate-700" strokeWidth={2} />
              Reminders
            </button>

            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 hover:text-emerald-600 transition flex items-center justify-center gap-1.5 cursor-pointer"
              title="Export Leads"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* ACTIVE PLAN & USAGE QUOTA CARD */}
        <div 
          onClick={() => navigate("/payment")}
          className="mb-4 relative overflow-hidden bg-zinc-950 text-white px-3.5 py-2 rounded-xl border border-zinc-800 flex items-center justify-between cursor-pointer hover:border-zinc-700 transition active:scale-[0.99] shadow-2xs"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border shrink-0 ${
              isExpired 
                ? "bg-rose-500/15 text-rose-400 border-rose-500/25" 
                : "bg-blue-500/15 text-blue-400 border-blue-500/25"
            }`}>
              {stats.plan_name}
            </span>
            <span className="text-[11px] text-zinc-400 font-medium truncate">
              Card Scans
            </span>
          </div>

          <div className="flex items-center gap-1.5 shrink-0 pl-2">
            <div className="font-mono text-xs font-bold flex items-center gap-0.5">
              {isUnlimited ? (
                <span className="text-zinc-100 text-[11px]">Unlimited</span>
              ) : (
                <>
                  <span className="text-zinc-100">{usedScans}</span>
                  <span className="text-zinc-500 font-normal text-[11px]">/</span>
                  {isExpired ? (
                    <span className="line-through text-zinc-500 decoration-zinc-400 text-[11px]">
                      {maxScans}
                    </span>
                  ) : (
                    <span className="text-zinc-300 text-[11px]">{maxScans}</span>
                  )}
                </>
              )}
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          </div>

          {!isUnlimited && (
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-zinc-800/80">
              <div 
                className={`h-full transition-all duration-300 ${
                  isExpired ? "bg-zinc-600" : percentUsed > 85 ? "bg-rose-500" : "bg-blue-500"
                }`}
                style={{ width: `${percentUsed}%` }}
              />
            </div>
          )}
        </div>

        {/* TAB SWITCHER */}
        <div className="flex bg-slate-100/80 p-1.5 rounded-[20px] mb-6">
          <button
            type="button"
            onClick={() => setActiveTab("pipeline")}
            className={`flex-1 py-2.5 rounded-2xl text-[13px] font-bold transition-all cursor-pointer ${
              activeTab === "pipeline" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            My Stats
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("team")}
            className={`flex-1 py-2.5 rounded-2xl text-[13px] font-bold transition-all cursor-pointer ${
              activeTab === "team" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Team Stats
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("chat")}
            className={`flex-1 py-2.5 rounded-2xl text-[13px] font-bold transition-all cursor-pointer ${
              activeTab === "chat" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Team Chat
          </button>
        </div>

        {/* TAB 1: PIPELINE */}
        {activeTab === "pipeline" && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div 
                onClick={() => setSelectedTag("All")}
                className={`bg-white border p-4 rounded-2xl shadow-sm flex flex-col justify-between h-28 cursor-pointer transition ${
                  selectedTag === "All" ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"
                }`}
              >
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">TOTAL</span>
                <span className="text-[40px] font-black text-slate-900 leading-none">
                  {stats.total || leads.length || 0}
                </span>
              </div>

              <div 
                onClick={() => setSelectedTag("Hot Lead")}
                className={`bg-white border p-4 rounded-2xl shadow-sm flex flex-col justify-between h-28 cursor-pointer transition ${
                  selectedTag === "Hot Lead" ? "border-red-600 ring-2 ring-red-600/10" : "border-slate-200"
                }`}
              >
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">HOT</span>
                <span className="text-[40px] font-black text-red-600 leading-none">
                  {stats.hotLeads || 0}
                </span>
              </div>

              <div 
                onClick={() => setSelectedTag("Follow-up")}
                className={`bg-white border p-4 rounded-2xl shadow-sm flex flex-col justify-between h-28 cursor-pointer transition ${
                  selectedTag === "Follow-up" ? "border-purple-600 ring-2 ring-purple-600/10" : "border-slate-200"
                }`}
              >
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">FOLLOW-UP</span>
                <span className="text-[40px] font-black text-purple-600 leading-none">
                  {stats.followUps || 0}
                </span>
              </div>
            </div>

            <div className="relative">
              <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
              <input
                type="text"
                placeholder="Search by name, company, email, phone"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-[14px] text-sm font-medium text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600 transition shadow-sm"
              />
            </div>

            <div className="flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <button
                type="button"
                onClick={() => setSelectedTag("All")}
                className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition cursor-pointer ${
                  selectedTag === "All"
                    ? "bg-black text-white shadow-sm"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                }`}
              >
                All
              </button>
              {PREDEFINED_TAGS.map((t) => (
                <button
                  type="button"
                  key={t.label}
                  onClick={() => setSelectedTag(t.label)}
                  className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition cursor-pointer ${
                    selectedTag === t.label
                      ? "bg-black text-white shadow-sm"
                      : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* PIPELINE LEAD LIST */}
            <div className="space-y-4 pt-2">
              {loading && leads.length === 0 ? (
                <div className="space-y-3.5">
                  <LeadCardSkeleton />
                  <LeadCardSkeleton />
                </div>
              ) : leads.length === 0 ? (
                <div className="py-12 bg-white border border-slate-200 rounded-[20px] text-center space-y-3 p-6 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center mx-auto">
                    <ScanLine className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base">No leads found</h3>
                  <p className="text-sm text-slate-500">Scan your first business card or adjust your filters.</p>
                  <button
                    type="button"
                    onClick={() => navigate("/capture")}
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-zinc-950 text-white font-bold text-xs uppercase tracking-wider hover:bg-zinc-800 transition active:scale-95 cursor-pointer mt-2"
                  >
                    <ScanLine className="h-4 w-4" /> Scan a card
                  </button>
                </div>
              ) : (
                <>
                  {leads.map((lead) => {
                    const leadId = lead.id || lead._id;
                    const leadName = lead.name || "Unnamed Lead";
                    const leadCompany = lead.company || "Independent";
                    const leadTitle = lead.job_title || lead.designation || "Contact";

                    const reminderState = (() => {
                      if (!lead.reminder_at) return null;
                      const due = new Date(lead.reminder_at).getTime();
                      const now = Date.now();
                      const day = 24 * 60 * 60 * 1000;
                      if (due < now - day) return { label: "Overdue", cls: "bg-red-100 text-red-700 border-red-200", icon: AlertTriangle };
                      if (due < now + day) return { label: "Due soon", cls: "bg-violet-100 text-violet-800 border-violet-200", icon: BellRing };
                      return null;
                    })();

                    return (
                      <div
                        key={leadId}
                        onClick={() => navigate(`/leads/${leadId}`)}
                        className="p-5 bg-white border border-slate-200 rounded-[24px] shadow-sm hover:shadow-md transition cursor-pointer relative"
                      >
                        <div className="flex items-start gap-4">
                          {typeof lead.image_base64 === "string" && lead.image_base64.trim() ? (
                            <img
                              src={lead.image_base64.startsWith("data:") ? lead.image_base64 : `data:image/jpeg;base64,${lead.image_base64}`}
                              alt={leadName}
                              loading="lazy"
                              decoding="async"
                              className="w-[60px] h-[60px] rounded-[14px] object-cover border border-slate-200 shrink-0 bg-slate-100"
                            />
                          ) : (
                            <div className="w-[60px] h-[60px] rounded-[14px] bg-slate-100 border border-slate-200 text-slate-700 font-black text-xl flex items-center justify-center shrink-0">
                              {leadName.charAt(0).toUpperCase()}
                            </div>
                          )}

                          <div className="space-y-1.5 min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-bold text-slate-900 text-[17px] leading-tight truncate">{leadName}</h3>
                              {reminderState && (
                                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${reminderState.cls}`}>
                                  <reminderState.icon className="h-3 w-3" /> {reminderState.label}
                                </span>
                              )}
                            </div>
                            
                            <p className="text-[13px] font-medium text-slate-500 truncate flex items-center gap-1.5">
                              <Building2 className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                              {leadTitle} • {leadCompany}
                            </p>

                            {lead.email && (
                              <p className="text-[13px] font-mono text-slate-500 truncate flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 shrink-0" strokeWidth={2} /> {lead.email.toLowerCase()}
                              </p>
                            )}
                            {lead.phone && (
                              <p className="text-[13px] font-mono text-slate-500 truncate flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 shrink-0" strokeWidth={2} /> {lead.phone}
                              </p>
                            )}
                          </div>
                        </div>

                        {Array.isArray(lead.tags) && lead.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
                            {lead.tags.slice(0, 4).map((t) => (
                              <span
                                key={t}
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${tagClass(t)}`}
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* LOAD MORE BUTTON */}
                  {hasMore && (
                    <div className="pt-2 pb-6">
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="w-full py-3.5 px-4 bg-white border border-slate-200 hover:border-slate-300 rounded-2xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition active:scale-[0.99] disabled:opacity-60 cursor-pointer shadow-2xs"
                      >
                        {loadingMore ? "Loading cards..." : "See more cards"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: TEAM STATS */}
        {activeTab === "team" && (
          <div className="space-y-4">
            {!hasCompany ? (
              <div className="p-6 bg-white border border-slate-200 rounded-[24px] text-center space-y-3 shadow-sm">
                <ShieldAlert className="w-8 h-8 text-amber-500 mx-auto" />
                <h3 className="text-slate-900 font-bold text-sm">Company Name Missing</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Set your <strong>Company Name</strong> in your profile to sync with colleagues.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/profile")}
                  className="px-4 py-2 bg-black text-white font-bold text-xs rounded-xl shadow-sm hover:bg-slate-800 transition cursor-pointer"
                >
                  Update Profile
                </button>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-[24px] overflow-hidden shadow-sm">
                <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">{companyName} Team</h3>
                    <p className="text-[11px] text-slate-500">Live Team Performance Directory</p>
                  </div>
                  <button 
                    type="button"
                    onClick={fetchInitialData} 
                    className="p-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-100 transition cursor-pointer"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-blue-600" : ""}`} />
                  </button>
                </div>

                {team.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-xs">
                    No team members logged under '{companyName}' yet.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {team.map((member) => (
                      <div key={member.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition">
                        <div>
                          <div className="font-bold text-slate-900 text-[13px] flex items-center gap-1.5">
                            {member.name || member.email}
                            {member.name === user?.name && (
                              <span className="bg-blue-100 text-blue-700 text-[9px] font-black px-2 py-0.5 rounded-full uppercase">You</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 font-medium">{member.role || "Exhibitor"}</div>
                        </div>

                        <div className="flex items-center gap-5 text-center">
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 block uppercase">Today</span>
                            <span className="font-mono font-black text-slate-900 text-sm">{member.todayScans || 0}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 block uppercase">Total</span>
                            <span className="font-mono font-black text-blue-600 text-sm">{member.totalScans || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: REAL-TIME CHAT */}
        <div className={activeTab === "chat" ? "block" : "hidden"}>
          <TeamChat 
            user={user} 
            companyName={companyName} 
            hasCompany={hasCompany} 
            activeTab={activeTab}
          />
        </div>

      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
}